import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Button, Card, Cascader, Space, Table } from 'antd';
import useService from '@/lib/hooks/useService';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import {
  needCareReservationColumns,
  reservationColumns,
} from '../tableColumns';
import AddReservationModal from '../AddReservationModal';
import styles from './index.module.scss';
import type { SingleValueType } from 'rc-cascader/lib/Cascader';
import type { ColumnsType } from 'antd/es/table';
import type { CarServiceReservation } from '@/types/database/carService';
import type {
  NeedCareDataType,
  ReservationDataType,
  ReservedUser,
} from '../../types';

interface AllTableDataType {
  title: string;
  dataSource: NeedCareDataType[] | ReservationDataType[];
  columns: ColumnsType<NeedCareDataType> | ColumnsType<ReservationDataType>;
}

dayjs.extend(advancedFormat);

export const UpdateReservationsContext = createContext<() => void>(() => {});

const TodayReservations = () => {
  const startOfTodayTimestamp = Number(dayjs().startOf('day').format('x'));
  const endOfTodayTimestamp = Number(dayjs().endOf('day').format('x'));

  const [users] = useService<ReservedUser>({ collectionName: 'users' });
  const [todayReservations, updateData] = useService<CarServiceReservation>({
    collectionName: 'carService',
    query: {
      $and: [
        { reservationTime: { $gt: startOfTodayTimestamp } },
        { reservationTime: { $lt: endOfTodayTimestamp } },
        { status: { $ne: '已取消' } },
      ],
    },
  });

  const [isOpenAddReservationModal, setIsOpenAddReservationModal] =
    useState<boolean>(false);
  const [reservationsForDisplay, setReservationsForDisplay] = useState<
    CarServiceReservation[]
  >([]);
  const [selectedHouseholdNumber, setSelectedHouseholdNumber] =
    useState<SingleValueType>([]);

  const getLatestReservations = useCallback(() => {
    updateData();
    setSelectedHouseholdNumber([]);
  }, [updateData]);

  useEffect(() => {
    setReservationsForDisplay(todayReservations);
  }, [todayReservations]);

  const getReservedUsers = useCallback((): ReservedUser[] => {
    const reservedUsers: ReservedUser[] = [];
    for (const reservation of reservationsForDisplay) {
      for (const index in users) {
        if (reservation.userId === users[index]._id) {
          reservedUsers.push(users[index]);
        }
      }
    }
    return reservedUsers;
  }, [reservationsForDisplay, users]);

  const reservedUsers = getReservedUsers();

  const householdNumbers = reservedUsers.map((user) => {
    return user.householdNumbers[0];
  });

  const cascaderOptions = generateHouseHoldNumberCascader(householdNumbers);

  const handleCascaderChange = (
    householdNumber: SingleValueType | undefined
  ) => {
    if (householdNumber === undefined) {
      getLatestReservations();
      setSelectedHouseholdNumber([]);
    } else {
      const filteredReservationsByHouseholdNumber =
        reservationsForDisplay.filter((reservation) => {
          const foundUser = users.find((user) => {
            return user._id === reservation.userId;
          });
          return foundUser?.householdNumbers[0] === householdNumber?.join('-');
        });
      setReservationsForDisplay(filteredReservationsByHouseholdNumber);
      setSelectedHouseholdNumber(householdNumber);
    }
  };

  const displayRender = (label: string[]) => {
    return label.join('-');
  };

  const closeModal = () => {
    setIsOpenAddReservationModal(false);
  };

  const getNeedCareData = useCallback(() => {
    const needCareReservations = reservationsForDisplay.filter(
      (reservation) => {
        return reservation.needCare === true && reservation.status !== '已下車';
      }
    );
    const needCareData = needCareReservations.map((reservation) => {
      const user = reservedUsers.find((user) => {
        return user._id === reservation.userId;
      });

      return {
        key: reservation._id,
        estimatedArrivalTime:
          dayjs(reservation.estimatedArrivalTime).format('YYYY-MM-DD HH:mm') ===
          'Invalid Date'
            ? ''
            : dayjs(reservation.estimatedArrivalTime).format(
                'YYYY-MM-DD HH:mm'
              ),
        phoneNumber: reservation.residentPhone,
        householdNumber: user?.householdNumbers[0],
        name: user?.name,
        status: reservation.status,
        action: ['查看', '新增進度'],
      };
    });
    return needCareData;
  }, [reservationsForDisplay, reservedUsers]);

  const getNotOnBoardData = () => {
    const notOnBoardReservations = reservationsForDisplay.filter(
      (reservation) =>
        reservation.status === '已預約' ||
        reservation.status === '已安排' ||
        reservation.status === '車輛已抵達'
    );
    const notOnBoardData = notOnBoardReservations.map((reservation) => {
      const user = reservedUsers.find((user) => {
        return user._id === reservation.userId;
      });
      const {
        _id,
        reservationTime,
        estimatedArrivalTime,
        status,
        fleetName,
        carNumber,
        driverName,
      } = reservation;

      return {
        key: _id,
        reservationTime: dayjs(reservationTime).format('YYYY-MM-DD HH:mm'),
        estimatedArrivalTime: estimatedArrivalTime
          ? dayjs(estimatedArrivalTime).format('YYYY-MM-DD HH:mm')
          : '',
        status,
        householdNumber: user?.householdNumbers[0],
        name: user?.name,
        fleetName,
        carNumber,
        driverName,
        action: ['查看', '新增進度', '取消'],
      };
    });
    return notOnBoardData;
  };

  const getOnBoardData = () => {
    const onBoardReservations = reservationsForDisplay.filter(
      (reservation) =>
        reservation.status === '已上車' || reservation.status === '已下車'
    );
    const onBoardData = onBoardReservations.map((reservation) => {
      const user = reservedUsers.find((user) => {
        return user._id === reservation.userId;
      });
      const {
        _id,
        reservationTime,
        estimatedArrivalTime,
        status,
        fleetName,
        carNumber,
        driverName,
      } = reservation;
      return {
        key: _id,
        reservationTime: dayjs(reservationTime).format('YYYY-MM-DD HH:mm'),
        estimatedArrivalTime: estimatedArrivalTime
          ? dayjs(estimatedArrivalTime).format('YYYY-MM-DD HH:mm')
          : '',
        status,
        householdNumber: user?.householdNumbers[0],
        name: user?.name,
        fleetName,
        carNumber,
        driverName,
        action: ['查看', '新增進度'],
      };
    });
    return onBoardData;
  };

  const allTableData: AllTableDataType[] = [
    {
      title: '待關懷下車',
      dataSource: getNeedCareData(),
      columns: needCareReservationColumns,
    },
    {
      title: '今日未上車',
      dataSource: getNotOnBoardData(),
      columns: reservationColumns,
    },
    {
      title: '今日已上車',
      dataSource: getOnBoardData(),
      columns: reservationColumns,
    },
  ];

  return (
    <UpdateReservationsContext.Provider value={updateData}>
      <Space direction="vertical" size="large" className={styles.pageContainer}>
        <Button onClick={() => setIsOpenAddReservationModal(true)}>
          新增叫車
        </Button>
        <AddReservationModal
          isOpenModal={isOpenAddReservationModal}
          closeModal={closeModal}
          updateReservations={getLatestReservations}
        />
        <Space>
          <p>戶號:</p>
          <Cascader
            options={cascaderOptions}
            placeholder="請選擇戶號"
            onChange={handleCascaderChange}
            displayRender={displayRender}
            value={selectedHouseholdNumber}
          />
        </Space>
        {allTableData.map((tableData, index) => (
          <Card key={index} className={styles.cardContainer}>
            <Space direction="vertical" className={styles.tableContainer}>
              <h3
                className={styles.boldTitle}
              >{`${tableData.title}住戶有${tableData.dataSource.length}組`}</h3>
              <Table
                columns={tableData.columns}
                dataSource={tableData.dataSource}
                scroll={{ x: 'max-content' }}
              />
            </Space>
          </Card>
        ))}
      </Space>
    </UpdateReservationsContext.Provider>
  );
};

export default TodayReservations;
